import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import SEO from '../../components/seo';
import { useGetPropertiesQuery, useGetSeoMarketsQuery } from '../../features/api/apiSlice';
import { FALLBACK_MARKETS, SITE_URL, citySlug } from '../../lib/seo';

const STATIC_LINKS = [
  { to: '/', label: 'Home' },
  { to: '/property-search-browse', label: 'All properties' },
  { to: '/for-rent', label: 'For rent' },
  { to: '/for-sale', label: 'For sale' },
  { to: '/land', label: 'Land' },
  { to: '/hostels', label: 'Hostels' },
  { to: '/hotels', label: 'Hotels' },
  { to: '/notice-board', label: 'Specific property requests' },
  { to: '/list-property', label: 'List a property' },
  { to: '/about', label: 'About' },
  { to: '/how-fees-work', label: 'How hunting fees work' },
  { to: '/contact', label: 'Contact' },
  { to: '/blog', label: 'Blog' },
  { to: '/guides', label: 'Guides' },
];

const HtmlSitemap = () => {
  const { data } = useGetPropertiesQuery({ limit: 100, offset: 0 });
  const properties = data?.data || [];
  const { data: markets = [] } = useGetSeoMarketsQuery();
  const cityLinks = useMemo(() => {
    if (markets.length) return markets;
    return FALLBACK_MARKETS.map((city) => ({ city, slug: citySlug(city) }));
  }, [markets]);

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO
        title="Sitemap"
        description="Browse every HireBroker page, city market, and live property listing in Uganda."
        canonicalUrl={`${SITE_URL}/sitemap`}
      />
      <Header />
      <main className="pt-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h1 className="text-3xl font-semibold text-slate-900">HireBroker sitemap</h1>
          <p className="mt-2 text-slate-600">
            Index of public pages and live listings for search engines and visitors.
          </p>

          <section className="mt-10">
            <h2 className="text-lg font-semibold text-slate-900 mb-3">Main pages</h2>
            <ul className="grid sm:grid-cols-2 gap-2">
              {STATIC_LINKS.map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className="text-sky-700 hover:underline">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </section>

          <section className="mt-10">
            <h2 className="text-lg font-semibold text-slate-900 mb-3">Cities</h2>
            <ul className="grid sm:grid-cols-2 md:grid-cols-3 gap-2">
              {cityLinks.map((market) => (
                <li key={market.slug || market.city}>
                  <Link
                    to={`/for-rent/${market.slug || citySlug(market.city)}`}
                    className="text-sky-700 hover:underline"
                  >
                    {market.city} rentals
                  </Link>
                  {' · '}
                  <Link
                    to={`/for-sale/${market.slug || citySlug(market.city)}`}
                    className="text-sky-700 hover:underline"
                  >
                    for sale
                  </Link>
                </li>
              ))}
            </ul>
          </section>

          <section className="mt-10">
            <h2 className="text-lg font-semibold text-slate-900 mb-3">Live listings</h2>
            <ul className="space-y-2">
              {properties.map((property) => (
                <li key={property.id || property.slug_id}>
                  <Link to={`/property/${property.slug_id}`} className="text-sky-700 hover:underline">
                    {property.title}
                  </Link>
                  {property.city ? (
                    <span className="text-slate-500 text-sm"> — {property.city}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default HtmlSitemap;
